import { IconButton, Ternary } from '@/components';
import { IconFont } from '@/components/IconFont';
import type { FormListFieldData, FormListOperation } from 'antd';
import { Button, Form, Radio, Space } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { ExtractTagSelect } from '../workflow/components/extract-tag-select';
import useExtractTags from '../workflow/components/extract-tag-select/use-extract-tags';
import CellItem from './cell-item';
import ColumnItem from './column-item';
import { INITIAL_RULE, RULE_MODE_OPTIONS } from './const';
import RowItem from './row-item/index';
import { useForm } from 'react-hook-form';
import { isNil } from 'lodash';

const { List: FormList } = Form;
const { Group: RadioGroup } = Radio;
const { Compact } = Space;

const StyleItem = Form.Item;
const FormListDirectItem = Form.Item;

const prefix = 'rules';

const FileAndRuleCard: FC<{
  filed: FormListFieldData;
  readonly: boolean;
  operation: FormListOperation;
  activeField: number;
  setActiveField: (key: number) => void;
}> = ({ filed, readonly, operation, activeField, setActiveField }) => {
  const { key, name } = filed;
  const form = Form.useFormInstance();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const tagCode: string = Form.useWatch(['rules', name, 'tag_code']);
  const { findTag, selectOptions } = useExtractTags();

  const tag = useMemo(
    () => findTag(tagCode, selectOptions),
    [tagCode, findTag, selectOptions],
  );

  useEffect(() => {
    // const currentValue = form.getFieldValue([...prefix]);
    const isTouched = form.isFieldTouched(['rules', name, 'tag_code']);
    if (isTouched) return;

    const tagCode = form.getFieldValue(['rules', name, 'tag_code']);
    if (!isNil(tagCode)) return;
    setIsCollapsed(false);
  }, []);

  return (
    <>
      {/* 未折叠 */}
      <div
        className={`relative  hover:shadow-md px-4 py-3 rounded transition
           border border-[#E5E7EC]
          ${activeField === key ? 'bg-[#f6f9ff]' : 'bg-white'}
          `}
        onClick={() => setActiveField(key)}
        key={key}
      >
        {/* 折叠前 */}
        <div
          style={{
            display: isCollapsed ? 'none' : 'block',
          }}
        >
          <FormListDirectItem
            label="字段"
            name={[name, 'tag_code']}
            rules={[
              {
                required: true,
                warningOnly: true,
                message: '',
              },
            ]}
            normalize={(value: string[]) => {
              return value?.[0];
            }}
            getValueProps={(value: string) => {
              return { value: [value] };
            }}
          >
            <ExtractTagSelect acceptedTypes={['text', 'table']} />
          </FormListDirectItem>
          <FormListDirectItem label="规则" name={[name, 'position_type']}>
            <RadioGroup
              block
              options={RULE_MODE_OPTIONS}
              optionType="button"
              buttonStyle="solid"
            />
          </FormListDirectItem>
          <FormListDirectItem shouldUpdate={() => true}>
            {(form) => {
              // const formValue =
              // form.getFieldsValue() as unknown as FormType;

              const position_type = form.getFieldValue([
                'rules',
                name,
                'position_type',
              ]);

              // const position_type =
              //   formValue?.[prefix]?.[name]?.position_type;

              switch (position_type) {
                case 'ROW':
                  return (
                    <RowItem
                      fullPrefix={[prefix, name, 'row_content_config']}
                      prefix={[name, 'row_content_config']}
                    />
                  );

                case 'COLUMN':
                  return (
                    <ColumnItem
                      fullPrefix={[prefix, name, 'col_content_config']}
                      prefix={[name, 'col_content_config']}
                    />
                  );

                case 'CELL':
                  return (
                    <CellItem
                      fullPrefix={[prefix, name, 'cell_content_config']}
                      prefix={[name, 'cell_content_config']}
                    />
                  );
              }

              return null;
            }}
          </FormListDirectItem>
        </div>

        {/* 折叠后 */}
        <div
          style={{ display: isCollapsed ? 'flex' : 'none' }}
          className="justify-between items-center gap-2"
        >
          <Ternary
            condition={Boolean(tag)}
            falseElement={<span className="text-slate-400">未选择字段</span>}
          >
            <Ternary
              condition={tag?.type === 'text'}
              falseElement={
                <div className="space-x-1">
                  <span>{tag?.parent_label}</span>
                  <span className="font-bold">{tag?.label}</span>
                </div>
              }
            >
              <span className="font-bold">{tag?.label}</span>
            </Ternary>
          </Ternary>
        </div>

        <>
          <IconButton
            active
            circle
            title="折叠"
            className="-top-3 right-[52px] absolute shadow hover:shadow-sm hover:scale-110"
            iconType="icon-zhankaikapian"
            style={{
              cursor: 'pointer',
              transform: isCollapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s',
            }}
            onClick={() => setIsCollapsed(!isCollapsed)}
          />
          {!readonly && (
            <IconButton
              active
              circle
              title="删除"
              className="-top-3 right-4 absolute shadow hover:shadow-md hover:text-error hover:scale-110"
              iconType="icon-shanchu1"
              onClick={() => operation.remove(name)}
            />
          )}
        </>
      </div>
    </>
  );
};

const FieldAndRuleItem: FC<{ readonly: boolean }> = ({ readonly }) => {
  // const { name } = fields;
  const [activeField, setActiveField] = useState<number>();
  return (
    <div>
      <StyleItem className="mb-0" label="字段和规则" shouldUpdate={() => true}>
        <FormList name={prefix}>
          {(fields, operation) => {
            return (
              <div className="space-y-6">
                {fields.map((field) => {
                  // const { key, name } = field;
                  // 注意：Form.List 会自动嵌套字段， 所以不用传前缀
                  // 但是 form.getFieldValue某个值时必须要传递正确的path

                  return (
                    <FileAndRuleCard
                      key={field.key}
                      filed={field}
                      readonly={readonly}
                      operation={operation}
                      activeField={activeField ?? 0}
                      setActiveField={setActiveField}
                    />
                  );
                })}

                <Button
                  block
                  className="bottom-0 sticky"
                  type="dashed"
                  htmlType="button"
                  icon={<IconFont type="icon-tianjia1" />}
                  onClick={() => operation.add(INITIAL_RULE)}
                >
                  新增抽取点
                </Button>
              </div>
            );
          }}
        </FormList>
      </StyleItem>
    </div>
  );
};

export default FieldAndRuleItem;
